import { useQuery, useInfiniteQuery, QueryKey, useQueries } from '@tanstack/react-query'
import makeApiCall from 'utils/api'
import {
  Order,
  orderStatus,
  OrderItems,
  OrderDetail,
  OrderRequest,
  Log,
  Cart,
  OrderRequestOrderItem,
} from 'types/orders'
import { ProductSearchResult } from 'types/treatments'

export function useOrders(
  orderStatus: orderStatus[],
  offset?: number,
  limit?: number,
  pharmacyId?: string,
  filters?: {
    from_date: string | undefined
    to_date: string | undefined
    date: string | undefined
  }
) {
  return useQuery<{ item_count: number; items: Order[] }>(
    ['orders', orderStatus, offset, limit, filters, pharmacyId],
    {
      queryFn: ({ queryKey }: { queryKey: QueryKey }) =>
        makeApiCall(`${queryKey[0]}`, '', '', {
          params: {
            order_status: orderStatus.join(','),
            offset,
            limit,
            pharmacy_id: pharmacyId,
            ...filters,
          },
        }),
    }
  )
}

export function useOrderItems(params: {
  pharmacy_id: string
  offset: number
  limit: number
  product_type: ProductSearchResult['product_type']
}) {
  return useQuery<{ items: OrderItems[]; item_count: number }>(['order-items', { ...params }], {
    queryFn: ({ queryKey }: { queryKey: QueryKey }) =>
      makeApiCall(`${queryKey[0]}`, '', '', {
        params,
      }),
  })
}

export function useOrder(id: string) {
  return useQuery<OrderDetail>([`/orders/${id}/details`], {
    queryFn: ({ queryKey }: { queryKey: QueryKey }) => makeApiCall(`${queryKey[0]}`),
  })
}

export function useMultipleOrderDetail(orderRequestOrders?: OrderRequestOrderItem[]) {
  const queries = useQueries({
    queries: orderRequestOrders
      ? orderRequestOrders.map((item) => {
          return {
            queryKey: [`/orders/${item.id}/details`, item.id],
            queryFn: async ({ queryKey }: { queryKey: QueryKey }) => {
              const data = await makeApiCall<OrderDetail>(`${queryKey[0]}`)
              return data
            },
          }
        })
      : [],
  })

  // Aggregate isLoading and isError states
  const isLoading = queries?.some((query) => query.isLoading)
  const isError = queries?.some((query) => query.isError)
  const data = queries?.map((item) => item.data)

  return {
    isLoading,
    isError,
    data,
    refetch: queries?.[0]?.refetch,
  }
}

export function useOrderRequests(
  orderStatus: orderStatus[],
  offset?: number,
  limit?: number,
  pharmacyId?: string
) {
  return useQuery<{ item_count: number; items: OrderRequest[] }>(
    ['order-requests', orderStatus, offset, limit],
    {
      queryFn: ({ queryKey }: { queryKey: QueryKey }) =>
        makeApiCall(`${queryKey[0]}`, '', '', {
          params: {
            request_status: orderStatus.join(','),
            offset,
            limit,
            pharmacy_id: pharmacyId,
          },
        }),
    }
  )
}

export function useInfiniteOrderRequests(
  orderStatus: orderStatus[],
  shouldFetch?: boolean,
  pharmacyId?: string
) {
  const fetchOrders = async ({ pageParam = 0 }) => {
    const res = await makeApiCall('/order-requests', '', '', {
      params: {
        order_status: orderStatus.join(','),
        offset: pageParam,
        limit: 5,
        pharmacy_id: pharmacyId,
      },
    })
    return res
  }

  return useInfiniteQuery<{ items: OrderRequest[]; item_count: number; offset: number }>({
    queryKey: ['order-requests', orderStatus, pharmacyId],
    queryFn: fetchOrders,
    getNextPageParam: (lastPage) => lastPage.offset + 5,
    enabled: shouldFetch,
  })
}

export function useOrderRequestOrders(id: string) {
  return useQuery<OrderRequestOrderItem[]>([`orders`, `requests`, id], {
    queryFn: ({ queryKey }: { queryKey: QueryKey }) =>
      makeApiCall(`${queryKey[0]}`, '', '', {
        params: {
          request_id: id,
          nopaginate: true,
        },
      }),
  })
}

export function useOrderLogs(id: string) {
  return useQuery<Log[]>([`/orders/${id}/logs`, id], {
    queryFn: ({ queryKey }: { queryKey: QueryKey }) => makeApiCall(`${queryKey[0]}`),
  })
}

export function useCart(payload: { offset?: number; limit?: number; pharmacyId?: string }) {
  return useQuery<{ item_count: number; items: Cart[] }>(
    ['orders', payload.limit, payload.offset],
    {
      queryFn: ({ queryKey }: { queryKey: QueryKey }) =>
        makeApiCall(`${queryKey[0]}`, '', '', {
          params: {
            order_status: 'initiated,awaiting approval',
            ...payload,
          },
        }),
    }
  )
}
